import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SessionStorageService } from 'ngx-webstorage';
import { lastValueFrom, Observable } from 'rxjs';

import { completeEndpoint, EndpointPart } from '../../core/global';
import { EndpointService } from '../endpoint/endpoint.service';

import { LoginModel } from './model/login.model';
import { User } from './model/user.model';
import { UserService } from './user.service';

@Injectable()
export class LoginService {
  private readonly userService = inject(UserService);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly injector = inject(Injector);
  private readonly http = inject(HttpClient);
  private readonly translate = inject(TranslateService);
  private readonly endpointService = inject(EndpointService);

  public static readonly LANGUAGE_QUERY_PARAM: string = 'userLanguage';
  public static readonly INSTANCE_QUERY_PARAM: string = 'startPkInstanz';

  public loginModel: LoginModel;

  /**
   * Router is only needed in special case in this service.
   * Therefore it is loaded by injector to avoid circular dependency using APP_INITALIZER.
   */
  public get router(): Router {
    return this.injector.get(Router);
  }

  public async getToken(): Promise<void> {
    return new Promise((resolve, reject): void => {
      let jwt: string;
      let userLanguage: string;

      if (this.getQueryVariable(LoginService.LANGUAGE_QUERY_PARAM) == null) {
        // eslint-disable-next-line no-restricted-globals
        userLanguage = window.navigator.language && window.navigator.language.startsWith('de') ? 'de' : 'en';
      } else {
        userLanguage = this.getQueryVariable(LoginService.LANGUAGE_QUERY_PARAM);
      }

      const userInstance = '0';

      if (userInstance && userLanguage) {
        lastValueFrom(this.loginUser(userInstance, userLanguage))
          .then((loginModel: LoginModel) => {
            this.loginModel = loginModel;
            if (this.loginModel.jwt === null) {
              // no login from server
              reject();
            } else {
              jwt = this.loginModel.jwt;
              this.userService.buildUserByToken(jwt, userLanguage);
              this.initializeUser(jwt, userLanguage, this.loginModel.version, this.loginModel.buildDate);
              resolve();
            }
          })
          .catch(error => {
            // An error message could be shown
            // eslint-disable-next-line no-console
            console.log(`error: ${error}`);
          });
      } else {
        if (this.loginFromSessionStorage()) {
          resolve();
        } else {
          // no login/user
          if (!this.sessionStorage.retrieve('userlanguage')) {
            // store default language
            this.sessionStorage.store('userlanguage', 'en_US');
          }
          reject();
        }
      }
    });
  }

  public getVersion(): string {
    return this.sessionStorage.retrieve('backendVersion');
  }

  public getBuildDate(): string {
    return this.sessionStorage.retrieve('buildDate');
  }

  private loginFromSessionStorage(): boolean {
    let jwt: string;
    let userLanguage: string;

    // check if user is still stored in session
    const userObjFromSession: User = this.sessionStorage.retrieve('user');
    if (userObjFromSession && userObjFromSession.token) {
      jwt = userObjFromSession.token;
      userLanguage = userObjFromSession.language;
      this.initializeUser(
        jwt,
        userLanguage,
        this.sessionStorage.retrieve('backendVersion'),
        this.sessionStorage.retrieve('buildDate'),
      );
      return true;
    } else {
      return false;
    }
  }

  private initializeUser(jwt: string, userLanguage: string, backendVersion: string, buildDate: string): void {
    // store user language
    this.sessionStorage.store('userlanguage', userLanguage);
    this.sessionStorage.store('backendVersion', backendVersion);
    this.sessionStorage.store('buildDate', buildDate);

    // set UI language on behalf of user language
    this.translate.use(userLanguage);
  }

  private getQueryVariable(variable: string): string {
    const name: string = variable.replaceAll(/[[\]]/g, '\\$&');
    const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
    // eslint-disable-next-line no-restricted-globals
    const results: RegExpExecArray = regex.exec(window.location.href);
    if (!results) {
      return null;
    }
    if (!results[2]) {
      return '';
    }
    return decodeURIComponent(results[2].replaceAll('+', ' '));
  }

  private loginUser(userInstance: string, userLanguage: string): Observable<LoginModel> {
    const requestParams = new HttpParams()
      .set(LoginService.INSTANCE_QUERY_PARAM, userInstance)
      .set(LoginService.LANGUAGE_QUERY_PARAM, userLanguage);
    const url: string = this.endpointService.getEndpointUrl(completeEndpoint(EndpointPart.LOGIN_SERVICE));
    return this.http.get<LoginModel>(url, { params: requestParams, withCredentials: true });
  }
}
